import type {FunctionComponent, SVGProps} from "react";
import {useCallback, useEffect, useRef} from "react";
import styled from "styled-components";
import {MathUtils} from "../../../utils/math/MathUtils";
import type {ISizeRange} from "../../modules/space/spaceeditor/logic3d/Constants";
import {Constants} from "../../modules/space/spaceeditor/logic3d/Constants";
import {SelectInputV5} from "../input/select/SelectInputV5";
import {colorPalette} from "../styles/colorPalette";
import {HorizontalAlignment} from "../../../utils/dom/DomUtils";
import {ReactUtils} from "../../utils/ReactUtils";
import {baseDistance, fontSize, radius} from "../styles/styles";

interface ISizeChangerProps {
	readonly disabled?: boolean;
	readonly value: number;
	readonly IconComponent?: FunctionComponent<SVGProps<SVGSVGElement> & {title?: string}>;
	readonly onChange: (newValue: number) => void;
	readonly range: ISizeRange;
	readonly isSpaceBarActionButton?: boolean;
}

const HiddenOptionValue = 1;

export const SizeChangerV5 = (props: ISizeChangerProps) => {
	const {range = Constants.SIZE.FONT, onChange, disabled, value, IconComponent, isSpaceBarActionButton} = props;

	const _inputRef = useRef<HTMLInputElement>(null);

	const updateInputValue = useCallback((newValue: number) => {
		_inputRef.current.value = newValue.toString();
	}, []);

	const setNewSize = useCallback(
		(newSize: number) => {
			if (!isNaN(newSize)) {
				newSize = MathUtils.clamp(newSize, range.min, range.max);
				if (newSize !== value) {
					onChange(newSize);
				}
				updateInputValue(newSize);
			} else {
				updateInputValue(value);
			}
		},
		[onChange, updateInputValue, range.min, range.max, value],
	);

	useEffect(() => {
		const onInputChange = (event: Event) => {
			/**
			 * For some reason, in react, `onChange=` seems to be triggered on every keypress.
			 * With this method, it"s only triggered when the input is finished (like pressing enter, or changing focus to another element)
			 */
			const inputElement = event.currentTarget as HTMLInputElement;
			const newFontSize = parseFloat(inputElement.value);

			setNewSize(newFontSize);
		};

		const inputElement = _inputRef.current;
		inputElement?.addEventListener("change", onInputChange);

		return () => {
			inputElement?.removeEventListener("change", onInputChange);
		};
	}, [setNewSize]);

	useEffect(() => {
		if (_inputRef.current) {
			if (Math.abs(parseFloat(_inputRef.current.value) - value) > Constants.EPSILON) {
				updateInputValue(value);
			}
		}
	}, [value, updateInputValue]);

	return (
		<SizeChangerStyled className="SizeChanger">
			{IconComponent && <IconComponent />}
			<input
				maxLength={`${range.max}`.length}
				ref={_inputRef}
				defaultValue={value.toString()}
				disabled={disabled}
				className={ReactUtils.cls({isSpaceBarActionButton})}
			/>
			<SelectInputV5
				options={range.values}
				selected={value}
				onChange={setNewSize}
				sort={false}
				searchBarMode="always off"
				dropdownOnly={true}
				horizontalAlignment={HorizontalAlignment.center}
			/>
		</SizeChangerStyled>
	);
};

export const SizeChangerStyled = styled.div`
	height: 32px;
	display: flex;
	flex-direction: row;
	align-items: center;
	border: 1px solid ${colorPalette.gray.c300};
	border-radius: ${radius.sm};
	font-size: ${fontSize.md};
	padding-left: ${baseDistance.sm};
	padding-right: ${baseDistance.xs};
	gap: ${baseDistance.sm};

	> svg:first-of-type {
		width: 16px;
		height: 16px;
	}

	input {
		background-color: inherit;
		color: ${colorPalette.gray.c950};
		border: none;
		width: 28px;

		&:focus {
			outline: none;
			border: none;
		}

		&.isSpaceBarActionButton {
			color: ${colorPalette.white};
		}
	}
`;
